import { useField } from 'formik';
import styled, { css } from 'styled-components';

import { TeamRole } from '../../../../../../../typings/TeamMember.interface';
import { usePermissionAwareDisabled } from '../../../contexts/TeamPermissionContext';
import { getStyledUtils, StyledUtilsProps } from '../../helpers/StyledUtils';

interface Props extends StyledUtilsProps {
  name: string;
  show_label?: boolean;
  disabled?: boolean;
  role?: TeamRole;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const StyledSwitch = styled.label<{ disabled?: boolean; has_label: boolean }>(
  ({ theme, disabled, has_label, ...props }) => css`
    ${getStyledUtils({ theme, ...props })}
    display: flex;
    font-weight: ${theme.font_weights.regular};
    line-height: ${theme.pxToRem(16)};
    margin: 0;

    ${!has_label &&
    css`
      margin: 0;
    `}
    input {
      ${has_label &&
      css`
        margin-left: ${theme.spacing(2)};
      `}

      border: none;
      appearance: none;
      width: ${theme.pxToRem(31)};
      height: ${theme.pxToRem(16)};

      border-radius: ${theme.radius.small};
      background-color: ${theme.colors.surface.container.neutral};
      position: relative;

      &:hover:not(:disabled) {
        cursor: pointer;
      }

      &:checked:not(:disabled) {
        border-color: ${theme.colors.surface.base.primary};
        background-color: ${theme.colors.surface.base.primary};
      }

      &:disabled {
        cursor: not-allowed;
      }

      &::after {
        content: '';
        position: absolute;
        right: 4px;
        top: 4px;
        bottom: 4px;
        left: unset;
        bottom: 0px;
        height: 6px;
        width: 6px;
        border-radius: 50%;
        border: 1px solid ${theme.colors.on.neutral.tertiary_neutral};
      }

      &:disabled {
        &::after {
          border-color: ${theme.colors.on.neutral.disabled};
        }
      }

      &::before {
        content: '';
        position: absolute;
        left: 1px;
        top: 1px;
        bottom: 1px;
        background-repeat: no-repeat;
        height: ${theme.pxToRem(14)};
        width: ${theme.pxToRem(14)};
        box-shadow: ${theme.shadows.neutral.base};
        border-radius: ${theme.radius.small_inset};
        background-color: ${theme.colors.on.hue.primary};
      }

      &:disabled {
        &::before {
          background-color: ${theme.colors.surface.base.disabled};
        }
      }

      &:checked {
        &::after {
          width: 1px;
          border-radius: 0;
          left: 8px;
          top: 4px;
          height: 8px;
          right: unset;
          background-color: ${theme.colors.on.hue.primary};
          border: none;
        }

        &:disabled {
          &::after {
            background-color: ${theme.colors.on.neutral.disabled};
          }
        }

        &::before {
          left: unset;
          right: 1px;
          box-shadow: ${theme.shadows.primary.base};
        }

        &:disabled {
          &::before {
            background-color: ${theme.colors.surface.base.disabled};
            box-shadow: ${theme.shadows.neutral.base};
          }
        }
      }

      &:focus {
        outline: none;
      }
      &:focus-visible {
        outline: 2px solid ${theme.colors.outline.focus.primary};
        outline-offset: 1px;
      }
    }

    ${disabled &&
    css`
      span:first-of-type {
        color: ${theme.colors.on.neutral.secondary_neutral};
      }
    `}
  `,
);

const SwitchInput: React.FC<Props> = ({ show_label, name, role, onChange, ...props }) => {
  const [field] = useField(name);
  const disabled = usePermissionAwareDisabled(props.disabled, role);

  return (
    <StyledSwitch as="label" has_label={!!show_label} {...props}>
      {show_label && (field.value ? 'On' : 'Off')}
      <input
        {...field}
        checked={!!field.value}
        disabled={disabled || props.disabled}
        onChange={(e) => {
          field.onChange(e);
          onChange?.(e);
        }}
        type="checkbox"
      />
    </StyledSwitch>
  );
};

export default SwitchInput;
